import React, { useState, useEffect, useRef } from "react";
import { useAgencyData } from "@/hooks/useAgencyData";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

const AgencyWhitelabel: React.FC = () => {
  const { t } = useTranslation("agency");
  const { agency, loading, updateAgency, refetchAgency } = useAgencyData();
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingLogoLight, setUploadingLogoLight] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lightFileInputRef = useRef<HTMLInputElement>(null);
  const faviconFileInputRef = useRef<HTMLInputElement>(null);

  const handleBrandingUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    variant: 'dark' | 'light' | 'favicon'
  ) => {
    const file = e.target.files?.[0];
    const inputRef =
      variant === 'dark' ? fileInputRef : variant === 'light' ? lightFileInputRef : faviconFileInputRef;
    const setUploading =
      variant === 'dark'
        ? setUploadingLogo
        : variant === 'light'
          ? setUploadingLogoLight
          : setUploadingFavicon;
    const field =
      variant === 'dark' ? 'logo_url' : variant === 'light' ? 'logo_url_light' : 'favicon_url';
    const fileName =
      variant === 'dark' ? 'logo' : variant === 'light' ? 'logo-light' : 'favicon';
    const successLabel =
      variant === 'dark' ? t('whitelabel.logoDark') : variant === 'light' ? t('whitelabel.logoLight') : t('whitelabel.favicon');

    if (!file || !supabase || !agency) return;

    const fileExt = (file.name.split('.').pop() || 'png').toLowerCase();
    const allowedTypes =
      variant === 'favicon'
        ? ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp', 'image/x-icon', 'image/vnd.microsoft.icon']
        : ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    const typeOk =
      allowedTypes.includes(file.type) ||
      (variant === 'favicon' && fileExt === 'ico' && (!file.type || file.type === 'application/octet-stream'));
    if (!typeOk) {
      toast.error(
        variant === 'favicon'
          ? t('whitelabel.unsupportedFormatFavicon')
          : t('whitelabel.unsupportedFormat')
      );
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('whitelabel.fileTooLarge'));
      return;
    }

    setUploading(true);
    try {
      const filePath = `${agency.id}/${fileName}.${fileExt}`;
      const contentType =
        file.type ||
        (fileExt === 'ico' ? 'image/x-icon' : `image/${fileExt === 'jpg' ? 'jpeg' : fileExt}`);

      const { error: uploadError } = await supabase.storage
        .from('agency-logos')
        .upload(filePath, file, { upsert: true, contentType });

      if (uploadError) {
        if (uploadError.message?.includes('Bucket not found')) {
          toast.error(t('whitelabel.bucketMissing'));
          return;
        }
        throw uploadError;
      }

      const { data: urlData } = supabase.storage
        .from('agency-logos')
        .getPublicUrl(filePath);

      if (!urlData?.publicUrl) {
        throw new Error(t('whitelabel.noPublicUrl'));
      }

      // Bust browser/CDN cache when replacing the same path
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      setFormData(prev => ({ ...prev, [field]: publicUrl }));

      const saved = await updateAgency({
        [field]: publicUrl,
        updated_at: new Date().toISOString(),
      });

      if (!saved) {
        toast.error(t('whitelabel.uploadedNotSaved'));
        return;
      }

      sessionStorage.removeItem('agency_cache');
      sessionStorage.removeItem('agency_cache_time');
      await refetchAgency();

      toast.success(t('whitelabel.uploadSuccess', { label: successLabel }));
    } catch (err: any) {
      console.error('Erro ao fazer upload:', err);
      toast.error(err?.message || t('whitelabel.uploadError'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleTestSmtp = async () => {
    if (!formData.smtp_host || !formData.smtp_email || !formData.smtp_port || !formData.smtp_password) {
      toast.error(t('whitelabel.smtpFillFirst'));
      return;
    }

    setTestingSmtp(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-smtp`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            smtp_host: formData.smtp_host,
            smtp_email: formData.smtp_email,
            smtp_port: parseInt(formData.smtp_port),
            smtp_ssl: formData.smtp_ssl === 'true',
            smtp_password: formData.smtp_password,
            test_email: formData.smtp_email,
          })
        }
      );

      const result = await response.json();
      if (result.success) {
        toast.success(t('whitelabel.smtpTestSent'));
      } else {
        toast.error(result.error || t('whitelabel.smtpTestError'));
      }
    } catch (err) {
      toast.error(t('whitelabel.smtpTestFailed'));
    } finally {
      setTestingSmtp(false);
    }
  };

  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    complement: "",
    city: "",
    state: "",
    domain: "",
    api_url: "",
    smtp_host: "",
    smtp_email: "",
    smtp_port: "",
    smtp_ssl: "true",
    smtp_password: "",
    logo_url: "",
    logo_url_light: "",
    favicon_url: "",
  });

  useEffect(() => {
    if (agency) {
      setFormData({
        company_name: agency.company_name || "",
        contact_name: agency.contact_name || "",
        email: agency.email || "",
        phone: agency.phone || "",
        address: agency.address || "",
        complement: agency.complement || "",
        city: agency.city || "",
        state: agency.state || "",
        domain: agency.domain || "",
        api_url: agency.api_url || "",
        smtp_host: agency.smtp_host || "",
        smtp_email: agency.smtp_email || "",
        smtp_port: agency.smtp_port?.toString() || "",
        smtp_ssl: agency.smtp_ssl ? "true" : "false",
        smtp_password: agency.smtp_password || "",
        logo_url: agency.logo_url || "",
        logo_url_light: agency.logo_url_light || "",
        favicon_url: agency.favicon_url || "",
      });
    }
  }, [agency]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const dataToUpdate: any = {
        company_name: formData.company_name,
        contact_name: formData.contact_name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address || null,
        complement: formData.complement || null,
        city: formData.city || null,
        state: formData.state || null,
        domain: formData.domain,
        api_url: formData.api_url,
        smtp_host: formData.smtp_host || null,
        smtp_email: formData.smtp_email || null,
        smtp_port: formData.smtp_port ? parseInt(formData.smtp_port) : null,
        smtp_ssl: formData.smtp_ssl === "true",
        smtp_password: formData.smtp_password || null,
        logo_url: formData.logo_url || null,
        logo_url_light: formData.logo_url_light || null,
        favicon_url: formData.favicon_url || null,
        updated_at: new Date().toISOString(),
      };

      const success = await updateAgency(dataToUpdate);

      if (success) {
        setMessage({ type: 'success', text: t('whitelabel.saved') });
      } else {
        setMessage({ type: 'error', text: t('whitelabel.saveError') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('whitelabel.saveError') });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]"></div>
          <p className="text-sm text-muted-foreground">{t('whitelabel.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground mb-2">
          {t('whitelabel.title')}
        </h3>
        <p className="text-muted-foreground text-sm">
          {t('whitelabel.subtitle')}
        </p>
      </div>

      {message && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-[#ECFDF5] border border-green-200 text-[#16A34A] dark:bg-green-500/10 dark:border-green-500/30 dark:text-[#22C55E]'
              : 'bg-[#FEF2F2] border border-red-200 text-[#DC2626] dark:bg-red-500/10 dark:border-red-500/30 dark:text-[#F87171]'
          }`}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="border border-border bg-card rounded-[20px] shadow-sm p-6">
          <h4 className="text-lg font-semibold text-foreground mb-6">
            {t('whitelabel.settings')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <Label htmlFor="company_name" className="text-sm font-medium text-foreground">
                {t('whitelabel.companyName')}
              </Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder={t('whitelabel.companyNamePlaceholder')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="contact_name" className="text-sm font-medium text-foreground">
                {t('whitelabel.contact')}
              </Label>
              <Input
                id="contact_name"
                value={formData.contact_name}
                onChange={(e) => handleInputChange('contact_name', e.target.value)}
                placeholder={t('whitelabel.contact')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="email" className="text-sm font-medium text-foreground">
                {t('whitelabel.email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder={t('whitelabel.email')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="phone" className="text-sm font-medium text-foreground">
                {t('whitelabel.phone')}
              </Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder={t('whitelabel.phone')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="address" className="text-sm font-medium text-foreground">
                {t('whitelabel.address')}
              </Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder={t('whitelabel.address')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="complement" className="text-sm font-medium text-foreground">
                {t('whitelabel.complement')}
              </Label>
              <Input
                id="complement"
                value={formData.complement}
                onChange={(e) => handleInputChange('complement', e.target.value)}
                placeholder={t('whitelabel.complement')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="city" className="text-sm font-medium text-foreground">
                {t('whitelabel.city')}
              </Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder={t('whitelabel.city')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="state" className="text-sm font-medium text-foreground">
                {t('whitelabel.state')}
              </Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                placeholder={t('whitelabel.state')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>
          </div>
        </div>

        <div className="border border-border bg-card rounded-[20px] shadow-sm p-6">
          <h4 className="text-lg font-semibold text-foreground mb-6">
            {t('whitelabel.section')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <Label htmlFor="logo_url" className="text-sm font-medium text-foreground">
                {t('whitelabel.logoDark')}
              </Label>
              <p className="text-xs text-muted-foreground -mt-1">
                {t('whitelabel.logoDarkHint')}
              </p>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => handleBrandingUpload(e, 'dark')}
              />
              <div className="flex gap-3">
                <Button
                  type="button"
                  className="rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploadingLogo ? t('whitelabel.uploading') : t('whitelabel.upload')}
                </Button>
                <Input
                  id="logo_url"
                  value={formData.logo_url}
                  onChange={(e) => handleInputChange('logo_url', e.target.value)}
                  placeholder={t('whitelabel.logoDarkPlaceholder')}
                  className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                />
              </div>
              {formData.logo_url && (
                <div className="mt-2 rounded-lg bg-[#18181B] p-3 inline-flex">
                  <img
                    src={formData.logo_url}
                    alt="Preview logo black"
                    className="max-h-[60px] object-contain rounded"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="logo_url_light" className="text-sm font-medium text-foreground">
                {t('whitelabel.logoLight')}
              </Label>
              <p className="text-xs text-muted-foreground -mt-1">
                {t('whitelabel.logoLightHint')}
              </p>
              <input
                type="file"
                ref={lightFileInputRef}
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => handleBrandingUpload(e, 'light')}
              />
              <div className="flex gap-3">
                <Button
                  type="button"
                  className="rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                  onClick={() => lightFileInputRef.current?.click()}
                  disabled={uploadingLogoLight}
                >
                  {uploadingLogoLight ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploadingLogoLight ? t('whitelabel.uploading') : t('whitelabel.upload')}
                </Button>
                <Input
                  id="logo_url_light"
                  value={formData.logo_url_light}
                  onChange={(e) => handleInputChange('logo_url_light', e.target.value)}
                  placeholder={t('whitelabel.logoLightPlaceholder')}
                  className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                />
              </div>
              {formData.logo_url_light && (
                <div className="mt-2 rounded-lg bg-[#F1F5F9] p-3 inline-flex">
                  <img
                    src={formData.logo_url_light}
                    alt="Preview logo white"
                    className="max-h-[60px] object-contain rounded"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="favicon_url" className="text-sm font-medium text-foreground">
                {t('whitelabel.favicon')}
              </Label>
              <p className="text-xs text-muted-foreground -mt-1">
                {t('whitelabel.faviconHint')}
              </p>
              <input
                type="file"
                ref={faviconFileInputRef}
                accept="image/png,image/jpeg,image/svg+xml,image/webp,image/x-icon,image/vnd.microsoft.icon,.ico"
                className="hidden"
                onChange={(e) => handleBrandingUpload(e, 'favicon')}
              />
              <div className="flex gap-3">
                <Button
                  type="button"
                  className="rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                  onClick={() => faviconFileInputRef.current?.click()}
                  disabled={uploadingFavicon}
                >
                  {uploadingFavicon ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploadingFavicon ? t('whitelabel.uploading') : t('whitelabel.upload')}
                </Button>
                <Input
                  id="favicon_url"
                  value={formData.favicon_url}
                  onChange={(e) => handleInputChange('favicon_url', e.target.value)}
                  placeholder={t('whitelabel.faviconPlaceholder')}
                  className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                />
              </div>
              {formData.favicon_url && (
                <div className="mt-2 rounded-lg bg-muted p-3 inline-flex items-center gap-3">
                  <img
                    src={formData.favicon_url}
                    alt="Preview favicon"
                    className="h-8 w-8 object-contain rounded"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className="text-xs text-muted-foreground">{t('whitelabel.faviconPreview')}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="domain" className="text-sm font-medium text-foreground">
                {t('whitelabel.domain')}
              </Label>
              <Input
                id="domain"
                value={formData.domain}
                onChange={(e) => handleInputChange('domain', e.target.value)}
                placeholder={t('whitelabel.domain')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                required
              />
            </div>

            <div className="flex flex-col gap-3 md:col-span-2">
              <Label htmlFor="api_url" className="text-sm font-medium text-foreground">
                {t('whitelabel.api')}
              </Label>
              <Input
                id="api_url"
                value={formData.api_url}
                onChange={(e) => handleInputChange('api_url', e.target.value)}
                placeholder={t('whitelabel.api')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                required
              />
            </div>
          </div>
        </div>

        <div className="border border-border bg-card rounded-[20px] shadow-sm p-6">
          <h4 className="text-lg font-semibold text-foreground mb-6">SMTP</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <Label htmlFor="smtp_host" className="text-sm font-medium text-foreground">
                {t('whitelabel.smtpHost')}
              </Label>
              <Input
                id="smtp_host"
                value={formData.smtp_host}
                onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                placeholder={t('whitelabel.smtpHost')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="smtp_email" className="text-sm font-medium text-foreground">
                {t('whitelabel.email')}
              </Label>
              <Input
                id="smtp_email"
                type="email"
                value={formData.smtp_email}
                onChange={(e) => handleInputChange('smtp_email', e.target.value)}
                placeholder={t('whitelabel.email')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="smtp_port" className="text-sm font-medium text-foreground">
                {t('whitelabel.smtpPort')}
              </Label>
              <Input
                id="smtp_port"
                type="number"
                value={formData.smtp_port}
                onChange={(e) => handleInputChange('smtp_port', e.target.value)}
                placeholder={t('whitelabel.smtpPort')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="smtp_ssl" className="text-sm font-medium text-foreground">
                {t('whitelabel.smtpSsl')}
              </Label>
              <Select
                value={formData.smtp_ssl}
                onValueChange={(value) => handleInputChange('smtp_ssl', value)}
              >
                <SelectTrigger className="h-11 rounded-xl text-sm text-[#0F172A] border border-[#E5E7EB] bg-white px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA]">
                  <SelectValue placeholder={t('whitelabel.smtpSsl')} />
                </SelectTrigger>
                <SelectContent className="rounded-xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA]">
                  <SelectItem value="true">{t('whitelabel.yes')}</SelectItem>
                  <SelectItem value="false">{t('whitelabel.no')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-3 md:col-span-2">
              <Label htmlFor="smtp_password" className="text-sm font-medium text-foreground">
                {t('whitelabel.smtpPassword')}
              </Label>
              <Input
                id="smtp_password"
                type="password"
                value={formData.smtp_password}
                onChange={(e) => handleInputChange('smtp_password', e.target.value)}
                placeholder={t('whitelabel.smtpPassword')}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            className="border-[#2563EB] text-[#2563EB] hover:bg-[#EEF4FF] hover:text-[#1D4ED8] dark:border-[#60A5FA] dark:text-[#60A5FA] dark:hover:bg-blue-500/15 min-w-[140px]"
            disabled={testingSmtp || !formData.smtp_host || !formData.smtp_email}
            onClick={handleTestSmtp}
          >
            {testingSmtp ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('whitelabel.testing')}
              </>
            ) : t('whitelabel.testSmtp')}
          </Button>
          <Button
            type="submit"
            className="rounded-xl bg-[#2563EB] text-white hover:bg-[#1D4ED8] min-w-[120px] transition-colors duration-200"
            disabled={saving}
          >
            {saving ? t('whitelabel.saving') : t('whitelabel.save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AgencyWhitelabel;
